import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Global, css } from '@emotion/react';
import { Divider, Popover, Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { FilterContextData } from 'contexts/FilterContext';
import {
    changeSelectAllOfCurrentAttrType,
    excludeIncludeEngine,
    removeAttrbuteNameByOnClick,
    removeEverythingForRespectiveAttrTypeOfApplyFilter,
} from 'parser/filterParser';
import { formatWord } from 'utils/helperMethods';

type FilterGroup = {
    attributeType: string;
    label: string;
    items: string[];
};

// Widen Ant Design popover content so the "All Selections" panel can use most of the viewport width
const globalFilterRibbonPopoverStyles = css`
    .global-filter-ribbon-popover.ant-popover .ant-popover-inner,
    .global-filter-ribbon-popover.ant-popover .ant-popover-inner-content {
        width: 100%;
        max-width: none;
        box-sizing: border-box;
    }
`;

const SettingIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="11" viewBox="0 0 14 11" fill="none">
        <path d="M2.66667 5.33333C3.90923 5.33333 4.95332 6.18318 5.24935 7.33333H12.6667C13.0349 7.33333 13.3333 7.63181 13.3333 8C13.3333 8.36819 13.0349 8.66667 12.6667 8.66667H5.24935C4.95332 9.81682 3.90923 10.6667 2.66667 10.6667C1.19391 10.6667 0 9.47276 0 8C0 6.52724 1.19391 5.33333 2.66667 5.33333ZM12 2.66667C12 1.93029 11.403 1.33333 10.6667 1.33333C9.93029 1.33333 9.33333 1.93029 9.33333 2.66667C9.33333 3.40305 9.93029 4 10.6667 4C11.403 4 12 3.40305 12 2.66667ZM1.33333 8C1.33333 8.73638 1.93029 9.33333 2.66667 9.33333C3.40305 9.33333 4 8.73638 4 8C4 7.26362 3.40305 6.66667 2.66667 6.66667C1.93029 6.66667 1.33333 7.26362 1.33333 8ZM13.3333 2.66667C13.3333 4.13943 12.1394 5.33333 10.6667 5.33333C9.4241 5.33333 8.38001 4.48349 8.08398 3.33333H0.666667C0.298477 3.33333 0 3.03486 0 2.66667C3.97364e-08 2.29848 0.298477 2 0.666667 2H8.08398C8.38001 0.849845 9.4241 0 10.6667 0C12.1394 0 13.3333 1.19391 13.3333 2.66667Z" fill="white" />
    </svg>
)

const ExpandIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.80466 4.19526C8.06501 4.45561 8.06501 4.87772 7.80466 5.13807L4.94273 8L7.80466 10.8619C8.06501 11.1223 8.06501 11.5444 7.80466 11.8047C7.54431 12.0651 7.1222 12.0651 6.86185 11.8047L3.52851 8.4714C3.26816 8.21106 3.26816 7.78894 3.52851 7.5286L6.86185 4.19526C7.1222 3.93491 7.54431 3.93491 7.80466 4.19526ZM12.4713 4.19526C12.7317 4.45561 12.7317 4.87772 12.4713 5.13807L9.60939 8L12.4713 10.8619C12.7317 11.1223 12.7317 11.5444 12.4713 11.8047C12.211 12.0651 11.7889 12.0651 11.5285 11.8047L8.19518 8.4714C7.93483 8.21106 7.93483 7.78894 8.19518 7.5286L11.5285 4.19526C11.7889 3.93491 12.211 3.93491 12.4713 4.19526Z"
            fill="white"
        />
    </svg>
)

/** Top ribbon for collapsible global filter: summary, collapse toggle, and popover to edit selections. */
function GlobalFilterRibbon() {
    const {
        isGlobalFilterCollapsed,
        setIsGlobalFilterCollapsed,
        allCheckedAttribute,
        applyFilterObject,
        setApplyFilterObject,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        attributeTypes,
        screenFilterConfig,
    } = useContext(FilterContextData);
    const [isSelectionPanelOpen, setIsSelectionPanelOpen] = useState(false);
    // Edits in the popover stay local until the user clicks Apply (avoids mutating committed filter state mid-edit)
    const [draftAllCheckedAttribute, setDraftAllCheckedAttribute] = useState<any>({});

    const disabledAttributeTypes: string[] = screenFilterConfig?.disabledAttributeTypes ?? [];

    const sourceCheckedAttribute = isSelectionPanelOpen ? draftAllCheckedAttribute : allCheckedAttribute;

    const appliedFilterGroups = useMemo<FilterGroup[]>(
        () =>
            Object.entries(sourceCheckedAttribute || {})
                .map(([attributeType, selectedValues]) => ({
                    attributeType,
                    label: formatWord(attributeType),
                    items: Object.keys((selectedValues as Record<string, unknown>) || {}),
                }))
                .filter((item) => item.items.length > 0),
        [sourceCheckedAttribute],
    );

    const openSelectionPanel = () => {
        setDraftAllCheckedAttribute(_.cloneDeep(allCheckedAttribute || {}));
        setIsSelectionPanelOpen(true);
    };

    const closeSelectionPanelWithoutSaving = () => {
        setDraftAllCheckedAttribute(_.cloneDeep(allCheckedAttribute || {}));
        setIsSelectionPanelOpen(false);
    };

    // Expanding the sidebar dismisses the popover and drops unsaved draft changes
    useEffect(() => {
        if (!isGlobalFilterCollapsed) {
            closeSelectionPanelWithoutSaving();
        }
    }, [isGlobalFilterCollapsed]);

    const removeSingleFilter = (attributeType: string, attributeName: string) => {
        if (disabledAttributeTypes?.includes(_.snakeCase(attributeType))) return;

        setDraftAllCheckedAttribute((oldData: any) =>
            removeAttrbuteNameByOnClick(oldData, attributeName, attributeType),
        );
    };

    // Commit draft removals: sync applyFilterObject / select-all flags for anything removed vs previous committed state
    const applyDraftChanges = () => {
        let nextApplyFilterObject = _.cloneDeep(applyFilterObject);
        let nextAllSelectedValueForAllAttribute = _.cloneDeep(allSelectedValueForAllAttribute);

        Object.keys(allCheckedAttribute || {}).forEach((attributeType) => {
            const committedObj = allCheckedAttribute?.[attributeType] || {};
            const draftObj = draftAllCheckedAttribute?.[attributeType] || {};
            const committedKeys = Object.keys(committedObj);
            const removedKeys = committedKeys.filter((key) => !(key in draftObj));

            if (!removedKeys.length) return;

            if (removedKeys.length === committedKeys.length) {
                nextApplyFilterObject = removeEverythingForRespectiveAttrTypeOfApplyFilter(
                    nextApplyFilterObject,
                    attributeType,
                    attributeTypes,
                );
                nextAllSelectedValueForAllAttribute = changeSelectAllOfCurrentAttrType(
                    nextAllSelectedValueForAllAttribute,
                    attributeType,
                );
                return;
            }

            removedKeys.forEach((attributeName) => {
                const selectedObj = committedObj?.[attributeName];
                if (!selectedObj) return;
                const eventPayload: any = { target: { checked: false } };
                const dimensionObj = { dimensionReference: selectedObj.dimension };

                excludeIncludeEngine(
                    selectedObj,
                    eventPayload,
                    attributeType,
                    nextApplyFilterObject,
                    (updatedApplyFilterObject: any) => {
                        nextApplyFilterObject = updatedApplyFilterObject;
                    },
                    dimensionObj,
                    nextAllSelectedValueForAllAttribute,
                );
            });
        });

        setAllCheckedAttribute(_.cloneDeep(draftAllCheckedAttribute));
        setApplyFilterObject(nextApplyFilterObject);
        setAllSelectedValueForAllAttribute(nextAllSelectedValueForAllAttribute);
        setIsSelectionPanelOpen(false);
    };

    const selectionPopoverContent = (
        <div className="w-full">
            <div className="flex items-center justify-between">
                <span className="text-[#140b1d] text-[17px] font-medium">All Selections</span>
                <button
                    type="button"
                    aria-label="Close selected filters panel"
                    className="text-[#140b1d] text-base cursor-pointer"
                    onClick={closeSelectionPanelWithoutSaving}
                >
                    <CloseOutlined />
                </button>
            </div>
            <Divider className="!my-2" />
            <div className="flex flex-col gap-3 py-2">
                {appliedFilterGroups.length > 0 ? (
                    appliedFilterGroups.map((group) => {
                        const isAttributeDisabled = disabledAttributeTypes?.includes(
                            _.snakeCase(group.attributeType),
                        );
                        return (
                            <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-1" key={group.attributeType}>
                                <span className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)] text-left">
                                    {group.label}:
                                </span>
                                <div className="flex flex-wrap">
                                    {group.items.map((item) => (
                                        <Tag
                                            key={`${group.attributeType}-${item}`}
                                            closable={!isAttributeDisabled}
                                            closeIcon={isAttributeDisabled ? false : undefined}
                                            className="[&_.ant-tag-close-icon]:!top-0"
                                            onClose={(event) => {
                                                event.preventDefault();
                                                removeSingleFilter(group.attributeType, item);
                                            }}
                                        >
                                            {item}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <span className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)]">No selections</span>
                )}
            </div>
            <Divider className="!my-2" />
            <div className="flex items-center justify-end gap-4">
                <span
                    className="cursor-pointer py-1 px-3 rounded rounded font-xs border border-borderGreyAl1"
                    onClick={closeSelectionPanelWithoutSaving}
                >
                    Close
                </span>
                <span
                    className="bg-purpleFont cursor-pointer py-1 px-3 rounded font-xs text-white"
                    onClick={applyDraftChanges}
                >
                    Apply
                </span>
            </div>
        </div>
    );

    return (
        <>
            <Global styles={globalFilterRibbonPopoverStyles} />
            <div className="w-full mb-4">
                <Popover
                    placement="bottomLeft"
                    trigger="click"
                    visible={isSelectionPanelOpen}
                    onVisibleChange={(visible) => {
                        if (visible) {
                            // Detail panel only when sidebar is collapsed; ignore open requests otherwise
                            if (!isGlobalFilterCollapsed) return;
                            openSelectionPanel();
                        } else {
                            closeSelectionPanelWithoutSaving();
                        }
                    }}
                    content={selectionPopoverContent}
                    align={{ offset: [20, -5] }}
                    overlayClassName="global-filter-ribbon-popover"
                    overlayStyle={{ zIndex: 1060, width: '98.2%' }}
                    overlayInnerStyle={{ width: '100%', borderRadius: '8px' }}
                >
                    <div
                        className={`h-[34px] w-full bg-[#fafafa] border-b border-[#e4e4e7] border-solid flex items-center ${isGlobalFilterCollapsed ? 'cursor-pointer' : 'cursor-default'
                            }`}
                    >
                        <button
                            type="button"
                            aria-label="Expand global filter"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsGlobalFilterCollapsed(!isGlobalFilterCollapsed);
                            }}
                            className="h-full w-10 rounded-r-md bg-purpleFont flex items-center justify-center cursor-pointer shrink-0"
                            style={{ boxShadow: '0px 2px 0px 0px rgba(95,73,188,0.1)', zIndex: 53 }}
                        >
                            {isGlobalFilterCollapsed ? <SettingIcon /> : <ExpandIcon />}
                        </button>
                        <div className="ml-3 flex min-w-0 flex-1 items-center gap-2 text-[12px] leading-[22px] overflow-hidden">
                            <span className="text-[#52525c] font-medium opacity-70 shrink-0">Applied Filters:</span>
                            {appliedFilterGroups.length > 0 ? (
                                <div className="min-w-0 flex items-center overflow-hidden whitespace-nowrap">
                                    {appliedFilterGroups.map((group, index) => (
                                        <React.Fragment key={group.attributeType}>
                                            {index > 0 && <Divider type="vertical" className="border-[#d4d4d8] mx-1" />}
                                            <span className="text-[#52525c] opacity-70 font-medium shrink-0">
                                                {group.label} ({group.items.length})
                                            </span>
                                        </React.Fragment>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-[#52525c] opacity-70 font-medium">No selections</span>
                            )}
                        </div>
                    </div>
                </Popover>
            </div>
        </>
    );
}

export default GlobalFilterRibbon;
